<template lang="pug">
  Drawer( :title="title" ref="drawer" @hide="$emit('hide')" )
    add-grouped-to-list-drawer(v-if="state === 'product'" :buyRequest="wishlistBuyRequest" @done="addItem" listType="Wishlist")
    .saving(v-if="state === 'loading'")
      Spinner(size="l" color="black")
      p {{__('Saving your items...')}}
    .success(v-if="state === 'success'")
      p {{__('You can update product quantities in your wishlist.')}}
      Message(status="success") {{__('Wishlist updated.')}}
    template(#footer)
      .buttons(v-if="state === 'success'")
        RogueButton.goTo(width="full" :link="'/account/wishlist/'" color="red") {{__('Go to Wishlist')}}
        RogueButton.done(width="full" @click="$emit('hide')") {{__('Done')}}
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useListStore } from '@/js/stores/listStore'
import { useProductStore } from '@/js/stores/productStore'
import { findDuplicateItem } from '@/js/stores/list-fns'
import { createBuyRequest } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import AddGroupedToListDrawer from '../add-grouped-to-list-drawer.vue'
import type { BuyRequest } from '@/js/services/RogueTypes'

const PICK_LIST = 'pick-list'
const CREATE = 'create'
const PRODUCT = 'product'

const props = withDefaults(defineProps<{
  buyRequest?: BuyRequest | null,
  validate?: ((buyRequest?: BuyRequest | BuyRequest[] | null) => boolean | Promise<boolean>) | null,
}>(), {
  buyRequest : null,
  validate   : null,
})

defineEmits<{ hide: [] }>()

const listStore = useListStore()
const productStore = useProductStore()
const rogue = useRogue()

const title = ref('')
const state = ref('')

// If we are adding a product to the wishlist
const wishlistBuyRequest = ref<BuyRequest | BuyRequest[] | null>(null)

const products = computed(() => productStore.products)
const wishlist = computed(() => listStore.wishlist)
const product = computed(() => products.value[props.buyRequest?.productId as number])

const items = computed<BuyRequest[]>(() => {
  if (Array.isArray(wishlistBuyRequest.value)) {
    return wishlistBuyRequest.value
  }

  return [wishlistBuyRequest.value as BuyRequest]
})

function setState(newState: string = CREATE) {
  state.value = newState
  title.value = rogue.__('Add to Wishlist')
  if (newState === PICK_LIST) {
    title.value = rogue.__('Add items to wishlist')
  }
}

async function addItem() {
  state.value = 'loading'
  if (props.buyRequest) {
    props.buyRequest.metadata = props.buyRequest.metadata || {}
  }

  if (props.validate && !(await props.validate(wishlistBuyRequest.value))) {
    setState(PRODUCT)

    return
  }

  const newItems = items.value
    .filter(i => !findDuplicateItem(i, wishlist.value.items))
    .map(i => Object.assign({}, i, { qty: Math.max(1, i.qty as number) }))

  listStore.addListItems(rogue, 'wishlist', newItems)

  if (newItems.length > 1 || props.buyRequest?.type === 'group') {
    setTimeout(() => {
      state.value = 'success'
    }, 200)
  }
}

if (product.value) {
  if (product.value.rhpaDisplayType === 'swatch') {
    wishlistBuyRequest.value = createBuyRequest({ ...product.value, rhpaDisplayType: undefined }, rogue)
  } else {
    wishlistBuyRequest.value = JSON.parse(JSON.stringify(props.buyRequest))
  }

  const children = (wishlistBuyRequest.value as BuyRequest).children

  if (product.value.type === 'configurable' || product.value.type === 'simple' || product.value.type === 'bundle') {
    setState(PICK_LIST)
  } else {
    children?.forEach(child => child.qty = 0)
    setState(PRODUCT)
  }
} else {
  setState(CREATE)
}
</script>
<style lang="scss" scoped>
  .success, .buttons, .saving {
    padding: $size-s;
  }

  .saving {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: $size-xs;
  }

  .buttons {
    z-index: 1;
    box-shadow: 0px -4px 3px rgb(50 50 50 / 25%);
    display: flex;
    flex-direction: row;
  }

  .buttons .goTo {
    width: 75%;
    margin-right: $size-s;
  }

  .buttons .done {
    width: 25%;
  }

  .flex {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: $size-m;
  }
</style>
